/**
 * Numeric stepper for bounded counts (installment interval days, quantities) — material-standard §3.3.
 */

export interface NumberStepperConfig {
  readonly max: number;
  readonly min: number;
  readonly step: number;
}

export interface NumberStepperState {
  readonly text: string;
  readonly value: number | null;
}

export type NumberStepperEvent =
  | { readonly type: 'blur' }
  | { readonly type: 'decrement' }
  | { readonly type: 'end' }
  | { readonly type: 'home' }
  | { readonly type: 'increment' }
  | { readonly type: 'page-decrement' }
  | { readonly type: 'page-increment' }
  | { readonly text: string; readonly type: 'input' };

export function clampStepperValue(value: number, config: NumberStepperConfig): number {
  return Math.min(config.max, Math.max(config.min, value));
}

function parseStepperText(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(
    trimmed.replace(/[۰-۹٠-٩]/g, (digit) => {
      const code = digit.charCodeAt(0);
      return String(code >= 0x06f0 ? code - 0x06f0 : code - 0x0660);
    }),
  );
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

export function createNumberStepperState(value: number | null = null): NumberStepperState {
  return {
    text: value === null ? '' : String(value),
    value,
  };
}

export function reduceNumberStepper(
  state: NumberStepperState,
  event: NumberStepperEvent,
  config: NumberStepperConfig,
): NumberStepperState {
  switch (event.type) {
    case 'input': {
      const parsed = parseStepperText(event.text);
      return { text: event.text, value: parsed };
    }
    case 'increment': {
      const base = state.value ?? config.min;
      const next = clampStepperValue(base + config.step, config);
      return { text: String(next), value: next };
    }
    case 'decrement': {
      const base = state.value ?? config.min;
      const next = clampStepperValue(base - config.step, config);
      return { text: String(next), value: next };
    }
    case 'page-increment': {
      const base = state.value ?? config.min;
      const next = clampStepperValue(base + config.step * 10, config);
      return { text: String(next), value: next };
    }
    case 'page-decrement': {
      const base = state.value ?? config.min;
      const next = clampStepperValue(base - config.step * 10, config);
      return { text: String(next), value: next };
    }
    case 'home': {
      return { text: String(config.min), value: config.min };
    }
    case 'end': {
      return { text: String(config.max), value: config.max };
    }
    case 'blur': {
      if (state.value === null) {
        return state;
      }
      const clamped = clampStepperValue(state.value, config);
      return { text: String(clamped), value: clamped };
    }
    default:
      return state;
  }
}
